import { useState, useEffect, useCallback } from 'react';

export interface DefaultWidths {
  leftPanel?: number;
  centerPanel?: 'auto' | number;
  rightPanel?: number;
}

export interface PanelLayoutState {
  leftPanelWidth: number;
  rightPanelWidth: number;
  leftPanelCollapsed: boolean;
  rightPanelCollapsed: boolean;
}

export const LAYOUT_STORAGE_KEYS = {
  LEFT_PANEL_WIDTH: 'fourPanelLayout_leftPanelWidth',
  RIGHT_PANEL_WIDTH: 'fourPanelLayout_rightPanelWidth',
  LEFT_PANEL_COLLAPSED: 'fourPanelLayout_leftPanelCollapsed',
  RIGHT_PANEL_COLLAPSED: 'fourPanelLayout_rightPanelCollapsed',
} as const;

// 安全解析函数
const safeParseNumber = (
  value: string | null,
  min: number,
  max: number,
  defaultValue: number
): number => {
  if (value === null) return defaultValue;
  const num = Number(value);
  return isNaN(num) || num < min || num > max ? defaultValue : num;
};

const safeParseBoolean = (value: string | null, defaultValue: boolean): boolean => {
  return value === null ? defaultValue : value === 'true';
};

/**
 * 面板布局状态管理Hook
 */
export const usePanelLayout = (defaultWidths: DefaultWidths = {}) => {
  const [state, setState] = useState<PanelLayoutState>({
    leftPanelWidth: defaultWidths.leftPanel || 280,
    rightPanelWidth: defaultWidths.rightPanel || 400,
    leftPanelCollapsed: false,
    rightPanelCollapsed: false,
  });

  useEffect(() => {
    try {
      setState({
        leftPanelWidth: safeParseNumber(
          localStorage.getItem(LAYOUT_STORAGE_KEYS.LEFT_PANEL_WIDTH),
          200, 600, defaultWidths.leftPanel || 280
        ),
        rightPanelWidth: safeParseNumber(
          localStorage.getItem(LAYOUT_STORAGE_KEYS.RIGHT_PANEL_WIDTH),
          300, 800, defaultWidths.rightPanel || 400
        ),
        leftPanelCollapsed: safeParseBoolean(
          localStorage.getItem(LAYOUT_STORAGE_KEYS.LEFT_PANEL_COLLAPSED),
          false
        ),
        rightPanelCollapsed: safeParseBoolean(
          localStorage.getItem(LAYOUT_STORAGE_KEYS.RIGHT_PANEL_COLLAPSED),
          false
        ),
      });
    } catch (error) {
      console.warn('Failed to load panel layout from localStorage:', error);
    }
  }, [defaultWidths.leftPanel, defaultWidths.rightPanel]);

  const saveState = useCallback((updates: Partial<PanelLayoutState>) => {
    try {
      const keyMapping: Record<keyof PanelLayoutState, string> = {
        leftPanelWidth: LAYOUT_STORAGE_KEYS.LEFT_PANEL_WIDTH,
        rightPanelWidth: LAYOUT_STORAGE_KEYS.RIGHT_PANEL_WIDTH,
        leftPanelCollapsed: LAYOUT_STORAGE_KEYS.LEFT_PANEL_COLLAPSED,
        rightPanelCollapsed: LAYOUT_STORAGE_KEYS.RIGHT_PANEL_COLLAPSED,
      };
      (Object.entries(updates) as [keyof PanelLayoutState, unknown][]).forEach(([key, value]) => {
        localStorage.setItem(keyMapping[key], String(value));
      });
    } catch (error) {
      console.warn('Failed to save panel layout to localStorage:', error);
    }
  }, []);

  const updateLeftPanelWidth = useCallback((width: number) => {
    const clampedWidth = Math.max(200, Math.min(600, width));
    setState((prev) => {
      saveState({ leftPanelWidth: clampedWidth });
      return { ...prev, leftPanelWidth: clampedWidth };
    });
  }, [saveState]);

  const updateRightPanelWidth = useCallback((width: number) => {
    const clampedWidth = Math.max(300, Math.min(800, width));
    setState((prev) => {
      saveState({ rightPanelWidth: clampedWidth });
      return { ...prev, rightPanelWidth: clampedWidth };
    });
  }, [saveState]);

  const toggleLeftPanel = useCallback(() => {
    setState((prev) => {
      const newCollapsed = !prev.leftPanelCollapsed;
      saveState({ leftPanelCollapsed: newCollapsed });
      return { ...prev, leftPanelCollapsed: newCollapsed };
    });
  }, [saveState]);

  const toggleRightPanel = useCallback(() => {
    setState((prev) => {
      const newCollapsed = !prev.rightPanelCollapsed;
      saveState({ rightPanelCollapsed: newCollapsed });
      return { ...prev, rightPanelCollapsed: newCollapsed };
    });
  }, [saveState]);

  return {
    state,
    updateLeftPanelWidth,
    updateRightPanelWidth,
    toggleLeftPanel,
    toggleRightPanel,
  };
};